import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarDays, Check, X, Trophy, GraduationCap, User } from "lucide-react";
import { toast } from "sonner";
import { addDays, format, startOfWeek, parseISO, isToday } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";

import { paymentLabel } from "@/components/PaymentMethodPicker";

const TYPE_STYLE = {
  booking: { paid: "bg-green-500/15 border-green-500/40 text-green-600 dark:text-green-400", unpaid: "bg-golden/15 border-golden/40 text-golden", icon: User },
  coaching: { paid: "bg-teal/15 border-teal/40 text-teal", unpaid: "bg-teal/10 border-teal/30 text-teal border-dashed", icon: GraduationCap },
  tournament: { paid: "bg-coral/15 border-coral/40 text-coral", unpaid: "bg-coral/15 border-coral/40 text-coral", icon: Trophy },
};

export default function AdminCalendar({ onChanged }) {
  const { t, dateFnsLocale } = useLanguage();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/calendar?start=${format(weekStart, "yyyy-MM-dd")}&days=7`).then(r => setData(r.data)).catch(() => {});
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const eventsAt = (date, slotStart) => (data?.events || []).filter(e => e.date === date && e.start <= slotStart && slotStart < e.end);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); setSelected(null); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const days = data?.dates || [];
  const slots = data?.slots || [];

  return (
    <Card className="border-border/50" data-testid="admin-calendar">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><CalendarDays size={18} className="text-coral" /> {t("cal.title")}</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full w-8 h-8" onClick={() => setWeekStart(d => addDays(d, -7))} data-testid="calendar-prev-week"><ChevronLeft size={14} /></Button>
          <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="calendar-today">{t("cal.today")}</Button>
          <span className="text-sm font-semibold min-w-[170px] text-center" data-testid="calendar-range">{format(weekStart, "d MMM", { locale: dateFnsLocale })} – {format(addDays(weekStart, 6), "d MMM yyyy", { locale: dateFnsLocale })}</span>
          <Button variant="outline" size="icon" className="rounded-full w-8 h-8" onClick={() => setWeekStart(d => addDays(d, 7))} data-testid="calendar-next-week"><ChevronRight size={14} /></Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <div className="min-w-[860px]">
          <div className="grid border-b border-border/40" style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}>
            <div />
            {days.map(d => {
              const dt = parseISO(d);
              return (
                <div key={d} className={`px-2 py-2 text-center border-l border-border/30 ${isToday(dt) ? "bg-coral/5" : ""}`}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{format(dt, "EEE", { locale: dateFnsLocale })}</p>
                  <p className={`text-base font-heading font-bold ${isToday(dt) ? "text-coral" : ""}`}>{format(dt, "d")}</p>
                </div>
              );
            })}
          </div>
          {slots.map(slot => (
            <div key={slot.start} className="grid border-b border-border/20" style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}>
              <div className="px-2 py-2 text-[11px] font-mono text-muted-foreground text-right pr-3">{slot.start}</div>
              {days.map(d => {
                const evs = eventsAt(d, slot.start);
                const ev = evs[0];
                const st = ev ? TYPE_STYLE[ev.type] : null;
                const cls = ev ? (ev.type === "tournament" ? st.paid : ev.payment_status === "paid" ? st.paid : st.unpaid) : "";
                return (
                  <div key={d + slot.start} className={`border-l border-border/20 p-1 min-h-[44px] ${isToday(parseISO(d)) ? "bg-coral/[0.03]" : ""}`}>
                    {ev ? (
                      <button onClick={() => setSelected(ev)} className={`w-full h-full text-left rounded-lg border px-2 py-1 transition-transform hover:scale-[1.02] ${cls}`} data-testid={`cal-event-${ev.type}-${ev.id}`}>
                        <p className="text-[11px] font-semibold truncate flex items-center gap-1"><st.icon size={10} /> {ev.title}</p>
                        <p className="text-[9px] opacity-80 truncate">{ev.type === "tournament" ? `${ev.teams} teams · ${ev.status}` : `${paymentLabel(ev.payment_method)} · ${ev.payment_status}`}</p>
                      </button>
                    ) : <div className="w-full h-full rounded-lg" data-testid={`cal-free-${d}-${slot.start}`} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 px-4 py-3 text-[10px] text-muted-foreground border-t border-border/30">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-golden/40 border border-golden/60" /> {t("cal.booking_pending")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500/40 border border-green-500/60" /> {t("cal.booking_paid")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal/40 border border-teal/60" /> {t("cal.lesson")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-coral/40 border border-coral/60" /> {t("cal.tournament")}</span>
        </div>
      </CardContent>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="calendar-event-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 capitalize">{selected.type === "coaching" ? t("cal.lesson") : selected.type === "tournament" ? t("cal.tournament") : t("ab.title")}</DialogTitle>
                <DialogDescription>{selected.date.split("-").reverse().join("-")} · {selected.start}–{selected.end}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p className="font-semibold">{selected.title}</p>
                {selected.type !== "tournament" && (
                  <>
                    <p className="text-muted-foreground text-xs">{t("cal.ref")} <span className="font-mono">{selected.reference}</span> · Rs {Number(selected.amount).toLocaleString()}</p>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="rounded-full text-[10px]">{paymentLabel(selected.payment_method)} · {selected.payment_status}</Badge>
                      <Badge className={`rounded-full text-[10px] ${selected.status === "confirmed" ? "bg-green-500/10 text-green-500" : "bg-golden/10 text-golden"}`}>{selected.status}</Badge>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                {selected.type === "tournament" ? (
                  <Link to={`/tournaments/${selected.id}`} className="flex-1"><Button className="w-full rounded-full bg-coral hover:bg-coral-600 text-white">{t("cal.open_tournament")}</Button></Link>
                ) : (
                  <>
                    {selected.payment_status !== "paid" && (
                      <Button className="flex-1 rounded-full bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="calendar-mark-paid"
                        onClick={() => run(() => api.put(selected.type === "booking" ? `/admin/bookings/${selected.id}/mark-paid` : `/admin/coaching/sessions/${selected.id}/mark-paid`), t("ab.marked_paid"))}><Check size={14} /> {t("cal.mark_paid")}</Button>
                    )}
                    <Button variant="outline" className="flex-1 rounded-full text-destructive border-destructive/30 gap-1" data-testid="calendar-cancel"
                      onClick={() => { if (window.confirm(t("ab.cancel_confirm"))) run(() => api.put(selected.type === "booking" ? `/admin/bookings/${selected.id}/cancel` : `/coaching/sessions/${selected.id}/cancel`), t("ab.cancelled")); }}><X size={14} /> {t("cal.cancel")}</Button>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
